import { gql, TypedDocumentNode } from "@apollo/client";
import { ParticipantsNode, ActivityType } from "./types";

export interface ActivityTypesQueryData {
  activityTypes: {
    edges: Array<{ node: ActivityType }>;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string;
      endCursor?: string;
    };
    totalCount: number;
  };
}

export interface ParticipantsQueryData {
  activityParticipations: {
    nodes: ParticipantsNode[];
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string;
      endCursor?: string;
    };
    totalCount: number;
  };
}

export const GET_ACTIVITY_TYPES = gql`
  query GetActivityTypes($first: Int, $after: String, $last: Int, $before: String, $filters: ActivityTypeFiltersInput) {
    activityTypes(first: $first, after: $after, last: $last, before: $before, filters: $filters) {
      edges {
        node {
          id
          title
          description
          createsEvent
          sectionAvailability
          config {
            creditRates {
              rateMultiplier
              rateUnit
            }
          }
          activities {
            totalCount
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_ACTIVITY_TYPE = gql`
  query GetActivityType($id: ID!) {
    activityType(id: $id) {
      id
      title
      description
      createsEvent
      sectionAvailability
      allowsParticipation
      config {
        creditRates {
          rateMultiplier
          rateUnit
          attendanceType
          participationRole
        }
      }
    }
  }
`;

export const GET_ACTIVITIES = gql`
  query GetActivities($first: Int, $after: String, $last: Int, $before: String, $filters: ActivityFiltersInput) {
    activities(first: $first, after: $after, last: $last, before: $before, filters: $filters) {
      nodes {
        id
        title
        participations {
          totalCount
        }
        allowParticipationStarting
        allowParticipationThrough
        managingSection {
          id
          name
        }
        event {
          slug
          title
          startsAt
          endsAt
          status
          facility {
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_ACTIVITY = gql`
  query GetActivity($id: ID!) {
    activity(id: $id) {
      id
      title
      internalDescription
      createdAt
      updatedAt
      allowParticipationStarting
      allowParticipationThrough
      duration
      checkinEnabled
      activityType {
        id
        title
        sectionAvailability
        createsEvent
        config {
          creditRates {
            rateMultiplier
            rateUnit
            attendanceType
            participationRole
            legacyCreditType
          }
        }
      }
      participations {
        totalCount
      }
      canClearAllParticipants
      managingSection {
        id
        name
      }
      event {
        slug
        title
        startsAt
        endsAt
      }
    }
  }
`;

export const GET_ACTIVITY_PARTICIPATION = gql`
  query GetActivityParticipation($id: ID!) {
    activityParticipation(id: $id) {
      id
      title
      description
      canEdit
      canDelete
      isTypeLevelParticipation
      role
      attendanceType
      beganAt
      endedAt
      quantity
      managingSection {
        id
        name
      }
      createdAt
      person {
        pgaId
        firstName
        lastName
      }
      activityType {
        id
        title
        description
        sectionAvailability
        createsEvent
        config {
          creditRates {
            id
            rateMultiplier
            rateUnit
            attendanceType
            participationRole
            legacyCreditType
          }
        }
      }
      activity {
        id
        title
        allowParticipationStarting
        allowParticipationThrough
        duration
        activityType {
          id
          title
          description
          sectionAvailability
          createsEvent
          config {
            creditRates {
              id
              rateMultiplier
              rateUnit
              attendanceType
              participationRole
              legacyCreditType
            }
          }
        }
        event {
          slug
          title
          startsAt
          endsAt
        }
      }
      credit {
        id
        activityCode
        creditsAccepted
        creditsAppliedFor
        description1
        description2
        creditType
      }
    }
  }
`;

export const GET_PARTICIPANTS = gql`
  query GetParticipants(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filters: ActivityParticipationFiltersInput
    $orderBy: BackofficeActivitiesActivityParticipationOrderByInput
  ) {
    activityParticipations(
      first: $first
      after: $after
      last: $last
      before: $before
      filters: $filters
      orderBy: $orderBy
    ) {
      nodes {
        id
        title
        canEdit
        canDelete
        role
        createdAt
        beganAt
        endedAt
        attendanceType
        isTypeLevelParticipation
        quantity
        managingSection {
          id
          name
        }
        credit {
          id
          creditsAccepted
        }

        person {
          pgaId
          firstName
          lastName
        }
        activity {
          id
          title
          activityType {
            title
            description
          }
          event {
            slug
            title
            startsAt
            endsAt
            status
            facility {
              name
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export const EXPORT_ACTIVITY_PARTICIPATIONS = gql`
  mutation ExportActivityParticipations($activityId: ID!) {
    exportActivityParticipations(input: { activityId: $activityId }) {
      success
      message
      url
    }
  }
`;

export const DELETE_ACTIVITY_PARTICIPATIONS = gql`
  mutation DeleteActivityParticipations($activityId: ID!) {
    deleteActivityParticipations(input: { activityId: $activityId }) {
      success
      message
      deletedCount
    }
  }
`;

export const EXPORT_STAGED_PARTICIPATIONS = gql`
  mutation ExportStagedParticipations($input: BackofficeActivitiesExportStagedParticipationsInput!) {
    exportStagedParticipations(input: $input) {
      success
      message
      url
    }
  }
`;

export const GET_STAGED_PARTICIPATIONS = gql`
  query GetStagedParticipations(
    $stagedImportId: ID!
    $filters: BackofficeActivitiesStagedParticipationsFiltersInput
    $filtersWithErrors: BackofficeActivitiesStagedParticipationsFiltersInput
    $first: Int
    $after: String
    $before: String
    $last: Int
  ) {
    stagedParticipations(stagedImportId: $stagedImportId, filters: $filters) {
      processingId
      finalizable
      totalCount
      participations(first: $first, after: $after, before: $before, last: $last) {
        nodes {
          id
          pgaId
          attendanceType
          startDate
          endDate
          participationRole
          processingId
          validationErrors
          name
          email
          profileImage
          creditsToBeEarned
          quantity
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        totalCount
      }
    }
    totalStagedParticipants: stagedParticipations(stagedImportId: $stagedImportId) {
      participations(first: $first, after: $after) {
        nodes {
          id
          validationErrors
        }
      }
      totalCount
    }
    errorsCount: stagedParticipations(stagedImportId: $stagedImportId, filters: $filtersWithErrors) {
      totalCount
    }
  }
`;

export const STAGE_PARTICIPATIONS = gql`
  mutation StageParticipations($input: BackofficeActivitiesStageParticipationsInput!) {
    stageParticipations(input: $input) {
      success
      message
      result {
        totalCount
        processingId
      }
    }
  }
`;

export const DELETE_STAGED_PARTICIPATION = gql`
  mutation DeleteStagedParticipation($input: BackofficeActivitiesDeleteStagedParticipationInput!) {
    deleteStagedParticipation(input: $input) {
      success
      message
      result {
        totalCount
        processingId
      }
    }
  }
`;

export const UPDATE_STAGED_PARTICIPATIONS = gql`
  mutation UpdateStagedParticipations($input: BackofficeActivitiesUpdateStagedParticipationsInput!) {
    updateStagedParticipations(input: $input) {
      success
      message
      result {
        totalCount
        processingId
      }
    }
  }
`;

export const FINALIZE_PARTICIPATIONS = gql`
  mutation FinalizeParticipations($input: BackofficeActivitiesFinalizeParticipationsInput!) {
    finalizeParticipations(input: $input) {
      success
      message
      result {
        totalCount
        processingId
      }
    }
  }
`;

export type StagedParticipationIdsResponse = {
  stagedParticipations: {
    participations: {
      nodes: Array<{ id: string }>;
    };
  };
};

export type StagedParticipationIdsVariables = {
  stagedImportId: string;
  filters?: {
    search?: string;
    hasValidationErrors?: boolean;
  };
};

export const CREATE_TYPE_LEVEL_PARTICIPATION = gql`
  mutation CreateActivityTypeParticipation(
    $activityTypeId: ID!
    $pgaId: String!
    $startDate: ISO8601DateTime!
    $endDate: ISO8601DateTime
    $quantity: Float!
    $title: String!
    $description: String
    $participationRole: ActivityParticipationRoleEnum!
    $attendanceType: AttendanceTypeEnum!
  ) {
    createActivityTypeParticipation(
      activityTypeId: $activityTypeId
      pgaId: $pgaId
      startDate: $startDate
      endDate: $endDate
      quantity: $quantity
      title: $title
      description: $description
      participationRole: $participationRole
      attendanceType: $attendanceType
    ) {
      success
      message
      participation {
        id
        title
        isTypeLevelParticipation
        quantity
      }
    }
  }
`;

export const GET_ALL_STAGED_PARTICIPATION_IDS: TypedDocumentNode<
  StagedParticipationIdsResponse,
  StagedParticipationIdsVariables
> = gql`
  query GetAllStagedParticipationIds(
    $stagedImportId: ID!
    $filters: BackofficeActivitiesStagedParticipationsFiltersInput
  ) {
    stagedParticipations(stagedImportId: $stagedImportId, filters: $filters) {
      participations {
        nodes {
          id
        }
      }
    }
  }
`;
